import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Input,
  Chip,
  Pagination,
  Spinner,
  Button,
} from "@nextui-org/react";
import { FiSearch } from "react-icons/fi";
import { MdOutlineFileDownload } from "react-icons/md";
import { displayDate, formatTime } from "../utils";
import { chipstyle, columns, statusOptions } from "../data/ordersData";
import { changeStatus } from "../store/slices/supplierSlice";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import axios from "axios";
import { STATUS } from "../store/constants";
import { refreshIfNeeded } from "../store/actions/refreshIfNeeded";

const INITIAL_VISIBLE_COLUMNS = [
  "status",
  "customer",
  "company",
  "car",
  "quantity",
  "total price",
  "created",
];

const Orders = () => {
  const role = localStorage.getItem("role");
  const dispatch = useDispatch();
  const { status } = useSelector((s) => s.supplier);

  const [orders, setOrders] = useState([]);
  const [filterValue, setFilterValue] = useState("");
  const [visibleColumns] = useState(new Set(INITIAL_VISIBLE_COLUMNS));
  const [statusFilter] = useState("all");

  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [page, setPage] = useState(() => {
    const stored = localStorage.getItem("currentPage");
    return stored ? Number(stored) : 1;
  });

  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  /* ---------------- sorting data ---------------- */
    const [sortDescriptor, setSortDescriptor] = useState({
    column: null,
    direction: null, // "ascending" | 
  });

  /* ---------------- batch & server-side ---------------- */
  const batchSize = 100;
  const [batchPage, setBatchPage] = useState(0);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const fetchingRef = useRef(false);

  const [searchTerm, setSearchTerm] = useState(""); // immediate input

  // stable refs for latest values so callbacks stay stable
  const filterRef = useRef(filterValue);
  const sortRef = useRef(sortDescriptor);
  const allDataLoadedRef = useRef(allDataLoaded);
  const batchPageRef = useRef(batchPage);

  useEffect(() => {
    filterRef.current = filterValue;
  }, [filterValue]);
  useEffect(() => {
    sortRef.current = sortDescriptor;
  }, [sortDescriptor]);
  useEffect(() => {
    allDataLoadedRef.current = allDataLoaded;
  }, [allDataLoaded]);
  useEffect(() => {
    batchPageRef.current = batchPage;
  }, [batchPage]);

  /* ---------------- Debounced search trigger (useRef timer) ---------------- */
  const searchTimerRef = useRef(null);

  const onSearchChange = useCallback((val) => {
    setSearchTerm(val); // keep input responsive

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      // set filter and reset pagination state after debounce
  setFilterValue(val.trim());
    setPage(1);
    setOrders([]);
    setAllDataLoaded(false);
    setBatchPage(0);
    localStorage.setItem("currentPage", 1);

      // note: effect below (listening on filterValue/sortDescriptor/rowsPerPage) will call fetchBatch(1)    searchTimerRef.current = null;
  }, 500);
}, []);
  const onClear = useCallback(() => {
    setSearchTerm("");
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
    setFilterValue("");
    setOrders([]);
    setAllDataLoaded(false);
    setBatchPage(0);
    setPage(1);
    localStorage.setItem("currentPage", 1);
    // effect will run and fetch with empty filterValue
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
    };
  }, []);

  /* ---------------- Fetch batch from API (stable, reads refs) ---------------- */
  const fetchBatch = useCallback(
    async (batchNum, { replace = false, filter, sort } = {}) => {
      const curFilter = typeof filter === "undefined" ? filterRef.current : filter;
      const curSort = typeof sort === "undefined" ? sortRef.current : sort;

      if (fetchingRef.current) return;
      if (allDataLoadedRef.current && !replace) return;

   try {
      fetchingRef.current = true;
     dispatch(changeStatus(STATUS.LOADING));
     await dispatch(refreshIfNeeded());
      const fresh = localStorage.getItem("accessToken");
      const auth = { headers: { Authorization: `Bearer ${fresh}` } };

      const qp = new URLSearchParams();
      qp.append("page", batchNum);
      qp.append("pageSize", batchSize);
      if (curFilter) qp.append("search", curFilter);
        if (curSort?.column) {
        qp.append("sortBy", curSort.column);
        qp.append("sortOrder", curSort.direction === "ascending" ? "asc" : "desc");
        }

      const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/orders/?${qp}`, auth);
      const fetchedRows = res.data?.details?.rows || [];
        const indexObj = res.data?.details?.index || {};
        const backendCurrentPage = indexObj.currentPage || batchNum;
        const backendTotalRecords = indexObj.totalRecords || 0;

        setTotalRecords(backendTotalRecords);
        setTotalPages(Math.ceil(backendTotalRecords / rowsPerPage));

        if (replace || batchNum === 1) {
          setOrders(fetchedRows);
        } else {
          setOrders((prev) => {
            const prevIds = new Set(prev.map((r) => r.id));
            const uniqueNew = fetchedRows.filter((r) => !prevIds.has(r.id));
            return [...prev, ...uniqueNew];
          });
        }

        setBatchPage(backendCurrentPage);
        batchPageRef.current = backendCurrentPage;

        if (fetchedRows.length < batchSize) {
          setAllDataLoaded(true);
          allDataLoadedRef.current = true;
        } else {
          setAllDataLoaded(false);
          allDataLoadedRef.current = false;
        }

      dispatch(changeStatus(STATUS.IDLE));
    } catch (err) {
      dispatch(changeStatus(STATUS.ERROR));
        console.error("fetchBatch error:", err?.response?.data || err.message || err);
      } finally {
        fetchingRef.current = false;
      }
    },
    [dispatch, batchSize, rowsPerPage]
  );

  /* ---------------- Ensure page data loaded ---------------- */
const ensurePageData = useCallback(
  async (uiPage) => {
    const requiredEndIndex = uiPage * rowsPerPage;
    if (requiredEndIndex <= orders.length) return;
    if (allDataLoadedRef.current) return;

    const requiredBatch = Math.ceil(requiredEndIndex / batchSize);
  let startBatch = batchPageRef.current || 0;
    for (let b = startBatch + 1; b <= requiredBatch; b++) {
      if (fetchingRef.current) {
        await new Promise((res) => {
          const id = setInterval(() => {
            if (!fetchingRef.current) {
              clearInterval(id);
              res();
            }
          }, 50);
        });
      }
      await fetchBatch(b);
      if (allDataLoadedRef.current) break;
    }
  },
  [orders.length, rowsPerPage, batchSize, fetchBatch]
);

// Trigger ensurePageData when page changes
useEffect(() => {
  ensurePageData(page).catch(console.error);
}, [page, ensurePageData]);

  /* ---------------- Effect: reset & fetch first batch when filter/sort/rowsPerPage change ---------------- */
  useEffect(() => {
    // already reset by onSearchChange/onClear, but ensure consistent state and fetch
    setOrders((prev) => (prev.length ? [] : prev));
    setAllDataLoaded(false);
    setBatchPage(0);
    setPage(1);

    // call fetchBatch explicitly with current refs
    fetchBatch(1, { replace: true, filter: filterRef.current, sort: sortRef.current }).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, sortDescriptor.column, sortDescriptor.direction, rowsPerPage]);

  /* ---------------- Sorting & items ---------------- */
  const filteredItems = useMemo(() => {
    let data = [...orders];
    if (
      statusFilter !== "all" &&
      Array.from(statusFilter).length !== statusOptions.length
    ) {
      data = data.filter((d) =>
        statusFilter.includes(d?.QuoteStatus?.status?.toLowerCase())
      );
    }
    return data;
  }, [orders, filterValue, statusFilter]);

 const getSortValue = (item, column) => {
  switch (column) {
    case "status":
      return item.QuoteStatus?.status?.toLowerCase() || "";
    case "customer":
     const first = item.Request?.User?.first_name || "";
      const last = item.Request?.User?.last_name || "";
      return `${first} ${last}`.trim().toLowerCase();
    case "car":
      const make =
        item.Request?.VehicleMake?.name?.toLowerCase() ||
        item.Request?.custom_make?.toLowerCase() ||
        "";
      const model =
        item.Request?.VehicleModel?.name?.toLowerCase() ||
        item.Request?.custom_model?.toLowerCase() ||
        "";
      return `${make} ${model}`.trim();
    case "quantity":
      return item.Request?.quantity || 0;
    case "total price":
      return item.Quote?.total_price || 0;
    case "created":
      return new Date(item.createdAt).getTime() || 0;
    default:
      return item[column] || "";
  }
};

const sortedItems = useMemo(() => {
  if (!sortDescriptor.column) return filteredItems;

  return [...filteredItems].sort((a, b) => {
    let v1 = getSortValue(a, sortDescriptor.column);
    let v2 = getSortValue(b, sortDescriptor.column);

    const isDate1 = !isNaN(Date.parse(v1));
    const isDate2 = !isNaN(Date.parse(v2));

    if (isDate1 && isDate2) {
      v1 = new Date(v1).getTime();
      v2 = new Date(v2).getTime();
    }

    const isNum1 = !isNaN(v1) && v1 !== "";
    const isNum2 = !isNaN(v2) && v2 !== "";

    if (isNum1 && isNum2) {
        return sortDescriptor.direction === "ascending" ? v1 - v2 : v2 - v1;
    }

    v1 = v1.toString().toLowerCase();
    v2 = v2.toString().toLowerCase();
    const cmp = v1.localeCompare(v2);
    return sortDescriptor.direction === "ascending" ? cmp : -cmp;
  });
}, [filteredItems, sortDescriptor]);

const pagedItems = useMemo(() => {
  const start = (page - 1) * rowsPerPage;
  return sortedItems.slice(start, start + rowsPerPage);
}, [sortedItems, page, rowsPerPage]);

  const pages = Math.max(1, Math.ceil(totalRecords / rowsPerPage));

  const headerColumns = useMemo(() => {
    let cols = [...columns];
    if (role === "SUPPLIER") cols = cols.filter((c) => c.uid !== "company");
    if (role === "CUSTOMER") cols = cols.filter((c) => c.uid !== "customer");
    return cols.filter((c) => visibleColumns.has(c.uid));
  }, [role, visibleColumns]);

  const handleDownloadCSV = () => {
    downloadCSV(pagedItems);
  };

  const renderCell = useCallback(
    (item, columnKey) => {
      switch (columnKey) {
        case "status":
          return (
            <Chip className="capitalize" color={chipstyle[item?.status]} variant="flat">
              {item?.OrderStatus?.status}
            </Chip>
          );
        case "company":
          return (
            role === "CUSTOMER" && (
              <p className="table_cell">{item.supplier?.company_name || "Not Provided"}</p>
            )
          );
        case "customer":
          return (
            role === "SUPPLIER" && (
              <p className="table_cell">
                {item.customer ? `${item.customer.first_name} ${item.customer.last_name}` : "Not Provided"}
              </p>
            )
          );
        case "car":
          return (
            <div className="flex flex-col gap-1">
              <p className="table_cell capitalize">
                {item?.Request?.VehicleMake?.name?.toLowerCase() || item?.Request?.custom_make?.toLowerCase() || "Not Provided"}{" "}
                {item?.Request?.VehicleModel?.name?.toLowerCase() || item?.Request?.custom_model?.toLowerCase() || "Not Provided"}
              </p>
              <p className="capitalize">
                {item?.Request?.Part?.name?.toLowerCase() || item?.Request?.custom_part_type?.toLowerCase()}
              </p>
            </div>
          );
        case "quantity":
          return <p className="table_cell">{item.Request?.quantity}</p>;
        case "total price":
          return <p className="table_cell">$ {item.Quote?.total_price}</p>;
        case "created":
          return (
            <div className="flex flex-col gap-1">
              <p className="table_cell capitalize">{formatTime(item.createdAt)}</p>
              <p className="table_cell_sm">{displayDate(item.createdAt)}</p>
            </div>
          );
        default:
          return item[columnKey];
      }
    },
    [role]
  );

  const onRowsPerPageChange = useCallback((e) => {
    const val = Number(e.target.value);
    setRowsPerPage(val);
    setPage(1);
  }, []);

  const downloadCSV = (data, filename = "orders_report.csv") => {
    const csvRows = [];
    const headers = ["Status", "Customer", "Car", "Quantity", "Total Price", "Created At"];
    csvRows.push(headers.join(","));

    for (const item of data) {
      const row = [
        item?.OrderStatus?.status || "N/A",
        item?.customer ? `${item.customer.first_name} ${item.customer.last_name}` : "N/A",
        `${item?.Request?.VehicleMake?.name || item?.Request?.custom_make || "N/A"} ${
          item?.Request?.VehicleModel?.name || item?.Request?.custom_model || "N/A"
        } - ${item?.Request?.Part?.name || item?.Request?.custom_part_type || "N/A"}`,
        item?.Request?.quantity || "N/A",
        `$${item?.Quote?.total_price || "0"}`,
        new Date(item?.createdAt).toLocaleString(),
      ];
      csvRows.push(row.map((v) => `"${v}"`).join(","));
    }

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const onPageChange = useCallback(
    (newPage) => {
      setPage(newPage);
      localStorage.setItem("currentPage", newPage);
      ensurePageData(newPage).catch(console.error);
    },
    [ensurePageData]
  );

  return (
    <Table
      aria-label="Orders Table"
      isHeaderSticky
      isStriped
      sortDescriptor={sortDescriptor}
      onSortChange={(newSort) => {setSortDescriptor(newSort);
        // sync ref will happen via effect; reset page to 1
        setPage(1);
        localStorage.setItem("currentPage", 1);
      }}
      topContent={
        <div className="flex flex-col gap-4 select-none">
          <div className="w-full flex justify-between gap-3 items-end">
            <Input
              isClearable
              className="flex-1 sm:max-w-[44%]"
              placeholder="Search Orders..."
              startContent={<FiSearch />}
              value={searchTerm}
              onClear={onClear}
              onValueChange={onSearchChange}
            />
            {role === "SUPPLIER" && (
              <Button
                startContent={<MdOutlineFileDownload />}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm"
                onClick={handleDownloadCSV}
              >
                Download CSV
              </Button>
            )}
          </div>
          <div className="flex justify-between text-xs sm:text-sm items-center">
            <span className="text-default-400">Total: {totalRecords} Orders</span>
            <label className="flex items-center text-default-400">
              Rows Per Page:
              <select className="bg-transparent outline-none text-default-400" onChange={onRowsPerPageChange}>
                <option value="6">6</option>
                <option value="10">10</option>
                <option value="15">15</option>
              </select>
            </label>
          </div>
        </div>
      }
      topContentPlacement="outside"
      bottomContent={
        <div className="py-2 flex justify-center items-center">
          <Pagination isCompact showControls showShadow color="primary" page={page} total={pages} onChange={onPageChange} />
        </div>
      }
      bottomContentPlacement="outside"
      classNames={{ td: "whitespace-nowrap" }}
    >
      <TableHeader columns={headerColumns}>
        {(col) => (
          <TableColumn
            key={col.uid}
            allowsSorting={
              col.uid === "status" ||
              col.uid === "company" ||
              col.uid === "car" ||
              col.uid === "quantity" ||
              col.uid === "total price" ||
              col.uid === "created"
            }
          >
            {col.name}
          </TableColumn>
        )}
      </TableHeader>

      <TableBody items={pagedItems} emptyContent="No orders found" isLoading={status === STATUS.LOADING && !orders.length} loadingContent={<Spinner label="Loading..." />}>
        {(item) => (
          <TableRow key={item.id}>
            {(colKey) => (
              <TableCell>
                <Link to={`/orders/${item.id}`}>{renderCell(item, colKey)}</Link>
              </TableCell>
            )}
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default Orders;
